// Phase 36 Batch T — Admin Analytics > Geo tab.
//
// Country / referrer / UTM breakdown. Top-10 lists for each, sorted
// by event count desc. No map — that would require a third-party
// vector dataset and out of scope for Batch T.

import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Globe2, ExternalLink, Megaphone, AlertTriangle } from "lucide-react";

function CountryFlag({ name }) {
  // Best-effort flag emoji from a country NAME isn't reliable — we
  // ship a plain badge instead. (The country_code lookup happens on
  // the backend; if it's null, name='Unknown'.)
  return (
    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-100 text-[10px] font-semibold uppercase text-slate-500">
      {(name || "??").slice(0, 2)}
    </span>
  );
}

function RankList({ title, icon: Icon, rows, labelKey, emptyHint }) {
  const max = rows?.[0]?.count || 1;
  return (
    <Card className="overflow-hidden">
      <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-3">
        <Icon className="h-4 w-4 text-indigo-600" />
        <h2 className="text-sm font-semibold text-slate-900">{title}</h2>
      </div>
      {!rows || rows.length === 0 ? (
        <p className="px-5 py-10 text-center text-sm text-slate-400">{emptyHint}</p>
      ) : (
        <ol className="divide-y divide-slate-100">
          {rows.map((r, i) => (
            <li key={String(r[labelKey]) + i} className="px-5 py-2.5">
              <div className="flex items-center justify-between gap-3 text-sm">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="w-5 text-right text-xs tabular-nums text-slate-400">{i + 1}.</span>
                  <span className="truncate font-medium text-slate-800">{r[labelKey] || "—"}</span>
                </div>
                <span className="shrink-0 tabular-nums text-slate-900">{r.count.toLocaleString()}</span>
              </div>
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-slate-100">
                <div
                  className="h-full rounded-full bg-indigo-500"
                  style={{ width: `${(100 * r.count) / max}%` }}
                />
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

export default function AdminAnalyticsGeo() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setErr(null);
    api
      .get(`/admin/analytics/geo?days=${days}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (!cancelled) setErr(e?.response?.data?.detail || e.message);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [days]);

  return (
    <div className="space-y-6" data-testid="admin-analytics-geo">
      <header className="flex items-end justify-between gap-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-indigo-600">Analytics · Geo</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">
            Where users come from
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Country distribution, top referrers and UTM attribution for the period.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Window</span>
          <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
            <SelectTrigger className="w-32" data-testid="geo-window-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last 365 days</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </header>

      {err && (
        <Card className="flex items-center gap-3 border-rose-200 bg-rose-50/60 p-4 text-sm text-rose-700">
          <AlertTriangle className="h-4 w-4" /> {err}
        </Card>
      )}

      {loading || !data ? (
        <Card className="flex h-72 items-center justify-center text-slate-400">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading geo…
        </Card>
      ) : (
        <>
          {/* Country breakdown with extra columns for signups + active users. */}
          <Card className="overflow-hidden">
            <div className="flex items-center gap-2 border-b border-slate-200 px-5 py-3">
              <Globe2 className="h-4 w-4 text-indigo-600" />
              <h2 className="text-sm font-semibold text-slate-900">Top countries</h2>
            </div>
            {!data.by_country?.length ? (
              <p className="px-5 py-10 text-center text-sm text-slate-400">
                No geo data yet. Existing users were created before IP-geo
                enrichment was wired — every new signup from today onward
                will resolve a country.
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-5 py-2 text-left">Country</th>
                    <th className="px-5 py-2 text-right">Events</th>
                    <th className="px-5 py-2 text-right">Active users</th>
                    <th className="px-5 py-2 text-right">Signups</th>
                  </tr>
                </thead>
                <tbody>
                  {data.by_country.map((c) => (
                    <tr key={c.country} className="border-t border-slate-100">
                      <td className="px-5 py-2">
                        <div className="flex items-center gap-2">
                          <CountryFlag name={c.country} />
                          <span className="font-medium text-slate-800">{c.country}</span>
                        </div>
                      </td>
                      <td className="px-5 py-2 text-right tabular-nums text-slate-900">
                        {c.count.toLocaleString()}
                      </td>
                      <td className="px-5 py-2 text-right tabular-nums text-slate-700">
                        {c.active_count.toLocaleString()}
                      </td>
                      <td className="px-5 py-2 text-right tabular-nums text-emerald-700">
                        {c.signup_count.toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {/* Referrer + UTM side-by-side. */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <RankList
              title="Top referrers"
              icon={ExternalLink}
              rows={data.by_referrer}
              labelKey="referrer"
              emptyHint="No referrer headers captured yet."
            />
            <RankList
              title="UTM sources"
              icon={Megaphone}
              rows={data.by_utm_source}
              labelKey="utm_source"
              emptyHint="No UTM-tagged traffic yet."
            />
            <RankList
              title="UTM mediums"
              icon={Megaphone}
              rows={data.by_utm_medium}
              labelKey="utm_medium"
              emptyHint="No UTM mediums tracked."
            />
            <RankList
              title="UTM campaigns"
              icon={Megaphone}
              rows={data.by_utm_campaign}
              labelKey="utm_campaign"
              emptyHint="No UTM campaigns tracked."
            />
          </div>
        </>
      )}
    </div>
  );
}
